'use client';

import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Search_RequestBody } from '@opensearch-project/opensearch/api';
import { QueryContainer } from '@opensearch-project/opensearch/api/_types/_common.query_dsl';
import { Sort } from '@opensearch-project/opensearch/api/_types/_common';
import { CardinalityAggregate } from '@opensearch-project/opensearch/api/_types/_common.aggregations';

import { InitialProductQueryFilters } from '@/constants';
import { EnumCustomerType, TreezStore } from '@/types';
import { ResolvedPrioritization } from '@/utils';
import { SearchResponseBody } from 'opensearch/services/search';

import { OpenSearchContext } from './OpenSearchContext';
import { useSearch } from '../hooks';
import {
  buildFacetQueries,
  buildPriceFilterFromQueryRecord,
  buildQueryFilterFromSearchParams,
  buildSearchQueryFilter,
  buildSmartFacetAggregations,
  mergeListingSortWithPrioritization,
} from '../utils';
import { CollapseParam } from '../constants';

const DEFAULT_PAGE_SIZE = 20;

interface ProviderProps extends PropsWithChildren {
  initialQueryFiltersByFacets?: Record<string, QueryContainer>;
  initialFixedQueryFiltersByFacets?: Record<string, QueryContainer>;
  initialSortFilter?: Sort;
  defaultSortFilter?: Sort;
  initialSearchFilter?: string;
  id?: string;
  selectedStore?: TreezStore;
  selectedCustomerType?: EnumCustomerType;
  includePromoItems?: boolean;
  initialPage?: number;
  /** From Prismic shop pages (category / strain / subcategory) when prioritization is enabled there. */
  prioritization?: ResolvedPrioritization;
  initialSearchResponse?: SearchResponseBody;
}

const OpenSearchProvider: FC<ProviderProps> = ({
  children,
  initialQueryFiltersByFacets = {},
  initialFixedQueryFiltersByFacets = {},
  initialSortFilter,
  defaultSortFilter,
  initialSearchFilter = '',
  id,
  selectedCustomerType,
  selectedStore,
  includePromoItems = false,
  initialPage = 0,
  prioritization,
  initialSearchResponse,
}) => {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [size, setSize] = useState(pageSize);
  const [from, setFrom] = useState<number>(initialPage * pageSize);
  const [sortFilter, setSortFilter] = useState<Sort | undefined>(
    initialSortFilter,
  );
  const [defaultSort, setDefaultSort] = useState<Sort | undefined>(
    defaultSortFilter,
  );
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [searchableAtributes, setSearchableAttributes] = useState<
    string[] | undefined
  >(undefined);
  const [queryFiltersByFacets, setQueryFiltersByFacets] = useState(
    initialQueryFiltersByFacets,
  );
  const [fixedQueryFiltersByFacets, setFixedQueryFiltersByFacets] = useState(
    initialFixedQueryFiltersByFacets,
  );
  const [linkPendingPage, setLinkPendingPage] = useState<number | null>(null);

  const listingCurrentPage = Math.floor(from / (pageSize || DEFAULT_PAGE_SIZE));

  useEffect(() => {
    if (linkPendingPage === null) return;
    if (listingCurrentPage === linkPendingPage) {
      setLinkPendingPage(null);
    }
  }, [linkPendingPage, listingCurrentPage]);

  useEffect(() => {
    const onPopState = () => setLinkPendingPage(null);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    setQueryFiltersByFacets(initialQueryFiltersByFacets);
  }, [initialQueryFiltersByFacets]);

  useEffect(() => {
    setFixedQueryFiltersByFacets(initialFixedQueryFiltersByFacets);
  }, [initialFixedQueryFiltersByFacets]);

  useEffect(() => {
    setSortFilter(initialSortFilter);
  }, [initialSortFilter]);

  useEffect(() => {
    setDefaultSort(defaultSortFilter);
  }, [defaultSortFilter]);

  useEffect(() => {
    setSearchFilter(initialSearchFilter);
  }, [initialSearchFilter]);

  useEffect(() => {
    setFrom(initialPage * pageSize);
  }, [initialPage, pageSize]);

  const priceFilter = buildPriceFilterFromQueryRecord(
    queryFiltersByFacets,
    includePromoItems,
  );

  const queryFiltersRecord = useMemo(
    () => ({
      ...buildSearchQueryFilter(searchFilter),
      ...buildQueryFilterFromSearchParams(InitialProductQueryFilters),
      ...queryFiltersByFacets,
      ...fixedQueryFiltersByFacets,
      ...priceFilter,
    }),
    [queryFiltersByFacets, fixedQueryFiltersByFacets, searchFilter],
  );

  const mergedSort = useMemo(
    () =>
      mergeListingSortWithPrioritization({
        prioritization,
        effectiveSort: sortFilter,
        listingDefaultSort: defaultSort,
        hasSearchText: Boolean(String(searchFilter ?? '').trim().length),
      }),
    [prioritization, sortFilter, defaultSort, searchFilter],
  );

  const searchBody: Search_RequestBody = {
    from,
    size,
    query: buildFacetQueries(queryFiltersRecord),
    aggs: buildSmartFacetAggregations(queryFiltersRecord),
    ...CollapseParam,
    ...(mergedSort ? { sort: mergedSort } : {}),
  };

  const { data, isLoading, status, refetch, isFetching } = useSearch({
    searchBody,
    id,
    selectedCustomerType,
    selectedStore,
    initialSearchResponse,
  });

  const hits = data?.hits?.hits ?? [];
  const totalHits =
    (data?.aggregations?.productsCount as CardinalityAggregate)?.value ?? 0;
  const aggregations = data?.aggregations ?? {};

  useEffect(() => {
    if (isLoading || isFetching) return;
    if (totalHits < from) setFrom(0);
  }, [totalHits, from]);

  return (
    <OpenSearchContext
      value={{
        searchBody,
        hits,
        totalHits,
        aggregations,
        queryFiltersByFacets,
        updateQueryFiltersByFacets: setQueryFiltersByFacets,
        fixedQueryFiltersByFacets,
        updateFixedQueryFiltersByFacets: setFixedQueryFiltersByFacets,
        isLoading,
        isFetching,
        pageSize,
        updatePageSize: setPageSize,
        updateSize: setSize,
        from,
        updateFrom: setFrom,
        linkPendingPage,
        setLinkPendingPage,
        isLinkNavigationPending: linkPendingPage !== null,
        searchableAtributes,
        updateSearchableAttributes: setSearchableAttributes,
        status,
        refetch,
        sortFilter,
        updateSortFilter: setSortFilter,
        searchFilter,
        updateSearchFilter: setSearchFilter,
      }}
    >
      {children}
    </OpenSearchContext>
  );
};

export { OpenSearchProvider };
